import { parseISO, startOfDay, endOfDay } from "date-fns";
import { toZonedTime, format as tzFormat, fromZonedTime } from "date-fns-tz";

export const getCurrentTimezoneOffset = (): string => {
  const offsetMinutes = -new Date().getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
  const minutes = String(absMinutes % 60).padStart(2, "0");
  return `${sign}${hours}:${minutes}`;
};

export const formatMinutesToDayHours = (time: number) => {
  const minutes = Math.floor(time);

  if (minutes === undefined || Number.isNaN(minutes)) {
    return "0 hour 0 minutes";
  }
  if (minutes < 24 * 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    const formattedHours = hours < 10 ? `0${hours}` : hours;
    const formattedMinutes =
      remainingMinutes < 10 ? `0${remainingMinutes}` : remainingMinutes;

    return `${formattedHours} ${
      hours === 1 || hours === 0 ? "hour" : "hours"
    } ${formattedMinutes} ${remainingMinutes === 1 ? "minute" : "minutes"}`;
  }

  const days = Math.floor(minutes / (24 * 60));
  const remainingMinutes = minutes % (24 * 60);
  const hours = Math.floor(remainingMinutes / 60);
  const formattedDays = days < 10 ? `0${days}` : days;
  const formattedHours = hours < 10 ? `0${hours}` : hours;
  return `${formattedDays} ${days === 1 ? "day" : "days"} ${formattedHours} ${
    hours === 1 ? "hour" : "hours"
  }`;
};

export const assignDatesToTabs = (tab: string | undefined) => {
  const currentTime = new Date();
  const year = currentTime.getFullYear();
  const month = currentTime.getMonth();
  const date = currentTime.getDate();
  const hours = currentTime.getHours();
  const minutes = currentTime.getMinutes();
  const seconds = currentTime.getSeconds();

  switch (tab) {
    case "last-24-hours":
      return {
        date_start: new Date(year, month, date - 1, hours, minutes, seconds),
        date_end: new Date(year, month, date, hours, minutes, seconds),
      };
    case "today":
      return {
        date_start: new Date(year, month, date, 0, 0, 0),
        date_end: new Date(year, month, date, hours, minutes, seconds),
      };
    case "last-7-days":
      return {
        date_start: new Date(year, month, date - 6, 0, 0, 0),
        date_end: new Date(year, month, date, hours, minutes, seconds),
      };
    case "last-30-days":
      return {
        date_start: new Date(year, month, date - 30, 0, 0, 0),
        date_end: new Date(year, month, date, hours, minutes, seconds),
      };
    case "jan":
      return {
        date_start: new Date(year, 0, 1, 0, 0, 0),
        date_end: new Date(year, 0, 31, 23, 59, 59),
      };
    case "apr-may":
      return {
        date_start: new Date(year, 3, 1, 0, 0, 0),
        date_end: new Date(year, 4, 31, 23, 59, 59),
      };
    case "aug-sep":
      return {
        date_start: new Date(year, 7, 1, 0, 0, 0),
        date_end: new Date(year, 8, 30, 23, 59, 59),
      };
    case "custom":
      return {
        date_start: new Date(year, month, date - 1, hours, minutes, seconds),
        date_end: new Date(year, month, date, hours, minutes, seconds),
      };
    default:
      return null;
  }
};

export const getTimeAgo = (timestamp: string): string => {
  const now = new Date();
  const past = new Date(timestamp);

  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

  const years = Math.floor(diffInSeconds / (365 * 24 * 60 * 60));
  if (years > 0) return `${years}y`;

  const weeks = Math.floor(diffInSeconds / (7 * 24 * 60 * 60));
  if (weeks > 0) return `${weeks}w`;

  const days = Math.floor(diffInSeconds / (24 * 60 * 60));
  if (days > 0) return `${days}d`;

  const hours = Math.floor(diffInSeconds / (60 * 60));
  if (hours > 0) return `${hours}h`;

  const minutes = Math.floor(diffInSeconds / 60);
  if (minutes > 0) return `${minutes}m`;

  return "1m";
};

export const callDurationFormat = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${mins.toString().padStart(2, "0")}:${secs
    .toString()
    .padStart(2, "0")}`;
};

export const convertUtcToLocalString = (
  utcTimestamp: string,
  format: string = "dd/MM/yyyy, hh:mm a",
  timezone?: string
) => {
  const date = parseISO(utcTimestamp);
  const userTimeZone =
    timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const localDate = toZonedTime(date, userTimeZone);
  return tzFormat(localDate, format, { timeZone: userTimeZone });
};

export const convertLocalToUtcIsoString = (localTimestamp: string): string => {
  const localDate = parseISO(localTimestamp);
  const userTimeZone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
  const utcDate = fromZonedTime(localDate, userTimeZone);
  return utcDate?.toISOString();
};

export const convertLocalToStartOfDay = (localTimestamp: string): string => {
  const localDate = parseISO(localTimestamp);
  const startOfDayLocal = startOfDay(localDate);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = tzFormat(startOfDayLocal, "yyyy-MM-dd", {
    timeZone: userTimeZone,
  });
  return `${formattedDate}T00:00:00.000Z`;
};

export const convertLocalToEndOfDay = (localTimestamp: string): string => {
  const localDate = parseISO(localTimestamp);
  const endOfDayLocal = endOfDay(localDate);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = tzFormat(endOfDayLocal, "yyyy-MM-dd", {
    timeZone: userTimeZone,
  });
  return `${formattedDate}T23:59:59.999Z`;
};

export const convertTimezoneToLocal = (
  date: string,
  time: string,
  timezone: string
): { date: string; time: string } => {
  if (!date || !time || !timezone) {
    return { date, time };
  }

  const sourceDateTime = `${date}T${time}:00`;
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const sourceZonedTime = fromZonedTime(sourceDateTime, timezone);
  const localZonedTime = toZonedTime(sourceZonedTime, userTimeZone);

  const localDate = tzFormat(localZonedTime, "yyyy-MM-dd", {
    timeZone: userTimeZone,
  });
  const localTime = tzFormat(localZonedTime, "HH:mm", {
    timeZone: userTimeZone,
  });

  return { date: localDate, time: localTime };
};

export const formatDateTimeInSentence = (
  dateTimeString: string,
  year?: string
) => {
  if (!dateTimeString) return "";

  try {
    const [datePart, timePart] = dateTimeString.split(" ");
    const [day, month] = datePart.split("/");
    const [hour, minute] = timePart.split(":");

    const targetYear = year ? parseInt(year) : new Date().getFullYear();
    const date = new Date(
      targetYear,
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute)
    );

    const dayNumber = parseInt(day);
    const monthAbbr = date.toLocaleDateString("en-US", { month: "short" });
    const time12Hour = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return `${dayNumber} ${monthAbbr} ${targetYear}, ${time12Hour}`;
  } catch {
    return dateTimeString;
  }
};

export const formatBillingStartDate = (date: Date): string => {
  const formattedDate = new Date(date);
  formattedDate.setDate(formattedDate.getDate() + 1);
  formattedDate.setUTCHours(0, 0, 0, 0);
  return formattedDate.toISOString();
};

export const formatBillingEndDate = (date: Date): string => {
  const formattedDate = new Date(date);
  formattedDate.setUTCHours(23, 59, 59, 999);
  return formattedDate.toISOString();
};

export const formatAsLocalTime = (
  dateStr: string,
  timeStr: string,
  timezone: string
): string => {
  const utcDate = new Date(`${dateStr}T${timeStr}:00Z`);
  const inTz = new Date(
    utcDate.toLocaleString("en-US", { timeZone: timezone })
  );
  const inUtc = new Date(utcDate.toLocaleString("en-US", { timeZone: "UTC" }));
  const offset = inUtc.getTime() - inTz.getTime();
  const correctedDate = new Date(utcDate.getTime() + offset);

  return correctedDate.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};
